<template>
    <article class="relative w-full rounded-12 max-md:bg-white/5">
        <BaseCartButton
            :product
            class="max-md:hidden" />

        <div
            class="group relative flex h-full overflow-hidden rounded-12 bg-white/5 transition-colors duration-300 focus-within:bg-white/15 md:hover:bg-white/15">
            <div class="bg-noise absolute inset-0 left-0 top-0"></div>

            <NuxtLink
                v-if="product.is_published"
                :to="`/catalog/${product.slug}`"
                target="_blank"
                class="absolute z-10 size-full" />

            <div class="flex flex-col">
                <div
                    class="relative flex h-full justify-between gap-3 overflow-hidden p-3 max-md:flex-col-reverse">
                    <div
                        class="size-[4.25rem] shrink-0 overflow-hidden rounded-12 max-sm:size-12 md:size-[6.25rem]">
                        <picture
                            v-if="imageSrc?.png"
                            class="size-full transition-transform duration-500 md:group-hover:scale-110">
                            <source
                                v-if="imageSrc.webp"
                                type="image/webp"
                                :srcset="imageSrc.webp" />
                            <img
                                loading="lazy"
                                :alt="imageSrc.alt || product.name"
                                :src="imageSrc.png"
                                class="size-full transition-transform duration-500 md:group-hover:scale-110" />
                        </picture>
                    </div>
                    <div
                        class="flex flex-col justify-between font-semibold md:w-full">
                        <h4
                            v-html="product.name"
                            :class="isCromi() ? 'pr-4' : ''"
                            class="line-clamp-3 hyphens-auto text-12/116 text-ash-200 md:text-16/120 lg:text-14/120 xl:text-16/120" />
                        <p
                            v-html="formatPrice(product.price)"
                            class="bottom-3 right-3 mt-2 text-right text-16/120 text-accent-100 transition-colors duration-300 max-md:absolute md:text-20/120 md:group-hover:text-ash-200" />
                    </div>
                </div>

                <div
                    v-if="isCromi()"
                    class="p-1">
                    <div
                        class="relative z-10 h-8 w-full max-w-64 overflow-hidden md:hidden">
                        <div class="">
                            <Transition name="view-slide-up">
                                <BaseLink
                                    v-if="!count"
                                    size="small"
                                    key="button"
                                    icon-right="cart-plus"
                                    class="h-full w-full"
                                    @click="addToCart({ slug: product?.slug })">
                                    В корзину
                                </BaseLink>

                                <div
                                    v-else
                                    key="count"
                                    class="relative flex rounded-full bg-black/20 backdrop-blur-sm">
                                    <span
                                        class="pointer-events-none absolute inset-0 rounded-full border border-ash-200"></span>

                                    <BaseCalculateInput
                                        key="input"
                                        size="sm"
                                        class="h-8 text-12/100"
                                        v-model="count"
                                        :min="0"
                                        :max="
                                            product?.max_cart_quantity ??
                                            undefined
                                        " />

                                    <NuxtLink
                                        to="/cart/"
                                        @click="closeToasts"
                                        aria-label="Перейти в корзину"
                                        class="flex size-8 shrink-0 items-center justify-center rounded-full border border-ash-200 bg-black/20 outline-none transition-[opacity,background-color] duration-300 hover:bg-ash-200/40 focus:bg-ash-200/40">
                                        <AtomIcon
                                            name="cart-to-go"
                                            class="size-4" />
                                    </NuxtLink>
                                </div>
                            </Transition>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </article>
</template>

<script setup lang="ts">
import { useToastStore } from '~/stores/toast.store'
import type { ImageSrcInterface } from '~/types/global.types'

import AtomIcon from '~/components/atom/atom-icon/AtomIcon.vue'
import BaseCalculateInput from '~/components/base/base-calculate-input/BaseCalculateInput.vue'
import BaseCartButton from '~/components/base/base-cart-button/BaseCartButton.vue'
import BaseLink from '~/components/base/base-link/BaseLink.vue'
import type { IProductCard } from '~/components/particles/product-card/product-card.types'

import { useCart } from '~/composables/useCart'
import { useCartCountModel } from '~/composables/useCartCountModel'
import { useCartProductLimits } from '~/composables/useCartProductLimits'

const props = defineProps({
    product: {
        type: Object as PropType<IProductCard>,
        required: true,
    },
})

const { closeAll: closeToasts } = useToastStore()

const { addToCart } = useCart()
const { applyProductLimit } = useCartProductLimits()
const count = useCartCountModel(() => props.product, { min: 0 })

watch(
    () => [props.product?.slug, props.product?.max_cart_quantity],
    () => {
        applyProductLimit(props.product)
    },
    {
        immediate: true,
    }
)

const imageSrc = computed<ImageSrcInterface | null>(() => {
    if (
        props.product.preview_image?.webp_url ||
        props.product.preview_image?.url
    ) {
        return {
            alt: props.product.preview_image.alt,
            webp: getMediaLink(props.product.preview_image.webp_url || '', 200),
            png: getMediaLink(props.product.preview_image.url || '', 200),
        }
    }

    if (props.product.images?.[0]?.webp_url || props.product.images?.[0]?.url) {
        return {
            alt: props.product.images[0].alt,
            webp: getMediaLink(props.product.images[0].webp_url || '', 200),
            png: getMediaLink(props.product.images[0].url || '', 200),
        }
    }

    return null
})
</script>
